import _ from 'underscore';
import { TARGETING_TYPE } from './targeting-type';
import { isRadiusUnitMiles } from './radius-unit';

// convert from a Bing Map entity type to a MT location type
const entityTypes = {
  1: 'CountryRegion',
  2: 'AdminDivision1',
  3: 'Postcode1',
  4: 'PopulatedPlace',
  5: 'Postcode1',
  null: 'PopulatedPlace',
  undefined: 'PopulatedPlace',
  '': 'PopulatedPlace',
  City: 'PopulatedPlace',
  Country: 'CountryRegion',
  SubGeography: 'AdminDivision1',
  County: 'AdminDivision2',
  PostalCode: 'Postcode1',
  MetroArea: 'AdminDivision2',
  AdministrativeRegionLevel1: 'AdminDivision1',
  FormerAdministrativeDivision: 'AdminDivision1',
  AdministrativeRegionLevel2: 'AdminDivision2',
  Neighborhood: 'Neighborhood',
  Postcode1: 'Postcode1',
  Postcode2: 'Postcode2',
  Postcode3: 'Postcode3',
  'Nielsen DMA': 'Nielsen DMA',
  'Microsoft Market Area': 'Microsoft Market Area',
};

/**
 * Consists of a location and its associated targeting location.
 */
export class LocationTarget {
  constructor(location, targetingType, radiusSize = null, radiusUnit = null) {
    this.location = location;
    this.targetingType = targetingType;
    this.radiusSize = radiusSize;
    this.radiusUnit = radiusUnit;

    // store any metadata that can be used to display error messages specific to the location target
    this.errors = [];

    // validate location
    if (location == null) {
      throw new Error('Location cannot be null.');
    }

    if (location.GeoPoint == null
      || location.GeoPoint.Longitude == null
      || location.GeoPoint.Latitude == null
    ) {
      throw new Error('Location longitude and latitude cannot be null.');
    }
  }

  /**
   * Determines whether the specified object is equal to the current LocationTarget.
   * @param {LocationTarget} obj - The object to compare with the current LocationTarget.
   * @returns {Boolean} - true if the specific object is equal to the current LocationTarget.
   */
  equals(obj) {
    return this.LocationId === obj.LocationId
      && this.LocationType === obj.LocationType
      && this.Latitude === obj.Latitude
      && this.Longitude === obj.Longitude
      && this.CountryCode === obj.CountryCode
      && this.targetingType === obj.targetingType
      && this.radiusSize === obj.radiusSize
      && this.radiusUnit === obj.radiusUnit;
  }

  /**
   * Generates a hashcode that can be used to identify a location target. Useful for comparing
   * different collections of LocationTarget instances.
   * @returns {String} - Hashcode of this LocationTarget instance.
   */
  getHashCode() {
    return JSON.stringify([
      this.LocationId,
      this.LocationType,
      this.Latitude,
      this.Longitude,
      this.CountryCode,
      this.targetingType,
      this.radiusSize,
      this.radiusUnit,
    ]);
  }

  get isRadiusLocationTarget() {
    return this.radiusSize != null;
  }

  get LocationId() {
    return this.location.Id;
  }

  get LocationType() {
    return this.location.Type;
  }

  get Latitude() {
    return this.location.GeoPoint.Latitude;
  }

  get Longitude() {
    return this.location.GeoPoint.Longitude;
  }

  get CountryCode() {
    return this.location.CountryCode;
  }

  get ExpandedName() {
    return this.location.ExpandedName;
  }

  get ParentLocations() {
    return this.location.ParentLocations;
  }

  /**
   * Generates the string parts needed for the location target grid row.
   * @param {Object} i18n - i18n library.
   * @returns {Object} - The radius description (used for the link text) and the location
   * description (used for the grey descriptive text) in the grid row.
   */
  getGridRowText(i18n) {
    let radiusDescription;
    let locationDescription;

    if (this.isRadiusLocationTarget) {
      let radiusUnitString = null;
      if (isRadiusUnitMiles(this.radiusUnit)) {
        radiusUnitString = i18n.getString(this.radiusSize === 1 ? _TL_('mile') : _TL_('miles'));
      } else {
        radiusUnitString = i18n.getString(this.radiusSize === 1 ? _TL_('kilometer') : _TL_('kilometers'));
      }

      // e.g. "25 miles around 1 Microsoft Way"
      radiusDescription = i18n.getString(_TL_('{{distance}} {{unit}}'), {
        distance: this.radiusSize,
        unit: radiusUnitString,
      });
      locationDescription = i18n.getString(_TL_('around {{locationName}}'), { locationName: this.ExpandedName });
    } else {
      // e.g. "Within Redmond, Washington"
      radiusDescription = i18n.getString(_TL_('Within'));
      locationDescription = this.ExpandedName;
    }

    return {
      radiusDescription,
      locationDescription,
    };
  }

  /**
   * Generates a full description of the location target with radius and location description.
   * @param {Object} i18n - i18n library.
   * @returns {String} - Full description of the location target, e.g. "Within Seattle, Washington".
   */
  getDescription(i18n) {
    const gridRowText = this.getGridRowText(i18n);

    return i18n.getString(_TL_('{{radiusDescription}} {{locationDescription}}'), gridRowText);
  }

  /**
   * Gets a localized description of the targeting type.
   * @param {Object} i18n - i18n library.
   * @returns {String} - Localized description of the targeting type, e.g. Excluded.
   */
  getTargetingTypeDescription(i18n) {
    switch (this.targetingType) {
      case TARGETING_TYPE.Included: return i18n.getString(_TL_('Targeted'));
      case TARGETING_TYPE.Excluded: return i18n.getString(_TL_('Excluded'));
      case TARGETING_TYPE.Recommended: return i18n.getString(_TL_('Recommended'));
      default: return '';
    }
  }

  /**
   * Gets the entityType used for fetching boundary polygons to display in the map view.
   * See: https://docs.microsoft.com/en-us/bingmaps/v8-web-control/modules/spatial-data-service-module/getboundaryrequestoptions-object
   * @returns {String} Entity type.
   */
  getEntityType() {
    // Prefer to use LesLocationType when fetching location boundaries
    const bingMapType = _.isEmpty(this.location.LesLocationType) ?
      this.location.Type : this.location.LesLocationType;
    return entityTypes[bingMapType];
  }

  static CreateIncludedLocationTarget(location, radiusSize, radiusUnit) {
    return new LocationTarget(location, TARGETING_TYPE.Included, radiusSize, radiusUnit);
  }

  static CreateExcludedLocationTarget(location, radiusSize, radiusUnit) {
    return new LocationTarget(location, TARGETING_TYPE.Excluded, radiusSize, radiusUnit);
  }

  static CreateFromExistingLocationTarget(existingLocationTarget, radiusSize, radiusUnit) {
    const locationTarget = new LocationTarget(
      existingLocationTarget.location,
      existingLocationTarget.targetingType,
      radiusSize,
      radiusUnit
    );

    locationTarget.Id = existingLocationTarget.Id;

    return locationTarget;
  }
}
